import { Injectable } from '@angular/core';
import { Observable, asapScheduler, of } from 'rxjs';
import { catchError, map, observeOn, shareReplay, tap } from 'rxjs/operators';

import { ApiService } from 'src/app/core/services/api.service';
import { SessionScopedState } from 'src/app/core/services/session/session-scoped-state';
import {
  ResearchConsentPage,
  ResearchConsentPatientStatus,
} from '../research-consent.types';

/**
 * The patient-facing consent endpoints.
 *
 * Every call passes `noAuth` because the patient has no account: the token from
 * the emailed link is the whole credential, and attaching a stray staff token
 * from the same browser would only muddy the audit trail on the backend.
 */
@Injectable({ providedIn: 'root' })
export class ResearchConsentService
  extends ApiService
  implements SessionScopedState
{
  public getPage(token: string): Observable<ResearchConsentPage> {
    return this.request({
      path: `public/research-consent/${encodeURIComponent(token)}`,
      method: 'GET',
      noAuth: true,
    });
  }

  public respond(token: string, agreed: boolean): Observable<ResearchConsentPage> {
    return this.request({
      path: `public/research-consent/${encodeURIComponent(token)}/respond`,
      method: 'POST',
      body: { agreed },
      noAuth: true,
    });
  }

  /**
   * A patient's consent position: whether their department runs the programme, and
   * their latest request.
   *
   * One call for both, and it needs only the patient's id — so it can run
   * alongside the patient fetch rather than after it, which is what keeps the
   * consent row from arriving a round trip behind the page.
   *
   * Staff-side, so it carries the normal token. Lives on `/consent-requests`
   * rather than under the patient: a consent request is its own resource with its
   * own lifecycle, and a patient accumulates several over time.
   */
  public getForPatient(
    patientId: number | string,
  ): Observable<ResearchConsentPatientStatus> {
    return this.request({
      path: `consent-requests/patient/${patientId}`,
      method: 'GET',
    });
  }

  /** Issue a fresh link for a patient and email it. Answers in the same shape. */
  public sendForPatient(
    patientId: number | string,
  ): Observable<ResearchConsentPatientStatus> {
    return this.request({
      path: 'consent-requests',
      method: 'POST',
      body: { patientId: Number(patientId) },
    });
  }

  /**
   * Whether a department may send the form. Staff-side call, so it goes out with
   * the normal token.
   *
   * Remembered per department, because the answer is a department setting rather
   * than anything about the patient in front of the user, and asking again on
   * every open bought nothing. What it is NOT is remembered past the session:
   * this map is dropped by `clearSessionState` at both ends of a sign-in, which
   * is the only reason keeping it is safe. Without that it outlived the user who
   * populated it — signing out is a route change, not a page load — and an
   * administrator switching the setting off was told it was still on by the very
   * screen they were checking.
   *
   * A remembered answer is still delivered asynchronously, exactly like a fetched
   * one. Answering synchronously made the timing depend on whether the question
   * had been asked before, and a caller that asks during its own change detection
   * — the new-patient dialog does, since the step publishes its department from
   * ngOnInit — then had a bound value change underneath a binding Angular had
   * already checked, which it refuses outright. A microtask is not a delay anyone
   * can see, and it makes the contract the same either way.
   *
   * Concurrent callers for the same department share one request rather than
   * racing; a failure is NOT remembered, so a blip does not disable the option for
   * the rest of the session.
   *
   * Any failure resolves to false rather than propagating: this only decides
   * whether to render an optional control, and a patient dialog that will not open
   * because a settings lookup failed is a worse outcome than one missing option.
   * The backend refuses the request regardless of what the UI showed.
   */
  public isEnabledForDepartment(
    departmentId: string | number,
  ): Observable<boolean> {
    if (!departmentId) {
      return of(false);
    }

    const key = String(departmentId);
    const known = this.enabledByDepartment.get(key);
    if (known !== undefined) {
      return of(known).pipe(observeOn(asapScheduler));
    }

    const pending = this.enabledInFlight.get(key);
    if (pending) {
      return pending;
    }

    const request = this.request({
      path: `departments/${key}`,
      method: 'GET',
    }).pipe(
      map((department) => !!department?.researchConsentEnabled),
      tap((enabled) => {
        this.enabledByDepartment.set(key, enabled);
        this.enabledInFlight.delete(key);
      }),
      catchError(() => {
        // Deliberately not remembered: the next ask reaches the network again.
        this.enabledInFlight.delete(key);
        return of(false);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.enabledInFlight.set(key, request);
    return request;
  }

  /**
   * Forget every department answer.
   *
   * In-flight lookups go too. One started by the user who is on their way out
   * would otherwise land after the next user has arrived and write their
   * predecessor's answer into the map.
   */
  public clearSessionState(): void {
    this.enabledByDepartment.clear();
    this.enabledInFlight.clear();
  }

  /** Departments whose setting has been resolved during this session. */
  private readonly enabledByDepartment = new Map<string, boolean>();
  /** In-flight lookups, so concurrent callers share one request. */
  private readonly enabledInFlight = new Map<string, Observable<boolean>>();
}
